import { createBounds, fromMutationObserver, fromResizeObserver, identity, noop } from '@socialive/common';
import { useEffect, useState } from 'react';
import { defer, EMPTY, fromEvent, iif, interval, merge } from 'rxjs';
import { delay, filter, map, take, throttleTime } from 'rxjs/operators';

const FPS_15 = 1_000 / 15;
const DEFAULT = { scroll: {} };

/**
 * @typedef {object} Bounds
 * @prop {number} x
 * @prop {number} y
 * @prop {number} width
 * @prop {number} height
 * @prop {number} top
 * @prop {number} bottom
 * @prop {number} left
 * @prop {number} right
 * @prop {object} scroll
 * @prop {number} scroll.width
 * @prop {number} scroll.height
 * @prop {number} scroll.top
 * @prop {number} scroll.bottom
 * @prop {number} scroll.left
 * @prop {number} scroll.right
 *
 * Watches for element resize, element scrolling, and optionally observes mutations and returns element bounding details.
 * @param {import('react').MutableRefObject} ref
 * @param {Object} [options={}]
 * @param {(HTMLElement)=>HTMLElement} [options.el=identity] - The element to receive the bounds for where `ref.current` is the input.
 * @param {(HTMLElement)=>HTMLElement} [options.resizer=identity]
 * @param {(HTMLElement)=>HTMLElement} [options.mutator=noop]
 * @returns {Bounds}
 */
export default (ref, { el = identity, resizer = identity, mutator = noop, intervalMs = FPS_15 } = {}) => {
  const [rect, setRect] = useState(DEFAULT);

  useEffect(() => {
    const onResize = () => setRect((cur) => createBounds(el(ref.current), cur) ?? DEFAULT);
    const sub = merge(
      interval(intervalMs).pipe(
        map(() => el(ref.current).offsetWidth),
        filter(Boolean),
        take(1)
      ),
      fromResizeObserver(resizer(ref.current)),
      fromEvent(ref.current, 'scroll'),
      iif(
        () => mutator(ref.current),
        defer(() => fromMutationObserver(mutator(ref.current), { childList: true })),
        EMPTY
      )
    )
      .pipe(throttleTime(intervalMs), delay(intervalMs))
      .subscribe(onResize);
    return () => sub.unsubscribe();
  }, [ref, el, resizer, mutator, intervalMs]);

  return rect;
};
